import { IFileUploadData } from '@northladder/services';
import { isWeb } from '@northladder/utilities';

import { ApiClient } from '../../../api-client';

import {
  formatAppraisalDetails,
  formatCreditLimitResponse,
  formatDealDetailsResponse,
  formatDealsListResponse,
  formatDeviceGradeChangeResponse,
  formatDeviceItemConditionResponse,
  formatRequestGradeDealsSearchResponse,
  formatGradeReviewRequestDetailsResponse,
  formatGradeReviewRequestHistoryResponse,
  formatGradeReviewRequestListResponse,
  formatTopModelType,
} from './query-hooks/transformers';
import {
  IBidExecutionTimeResponse,
  ICreateGradeChangeRequestPayload,
  ICreateGradeChangeRequestResponse,
  ICreditLimitResponse,
  IDealDetailsResponse,
  IDealsListResponse,
  IDeviceGradeConditionRawResponse,
  IDeviceItemConditionRawResponse,
  IExportDealsQueryOptions,
  IFetchAllTopBrands,
  IFetchAppraisalDetailsResponse,
  IFetchAppraisalsResponse,
  IFetchDealsUrlOptions,
  IFetchDeviceGradeConditionUrlOptions,
  IFetchGradeDealsUrlOptions,
  IFetchGradeReviewUrlOptions,
  IFetchPaginatedAppraisalsPayload,
  IFetchSummary,
  IFetchSummaryPayload,
  IFetchTopBrandsPayload,
  IFileUploadPayload,
  IGetSingedUrlResponse,
  IGradeDealsSearchRawResponse,
  IGradeReviewDetailsResponse,
  IGradeReviewHistoryResponse,
  IGradeReviewRequestListResponse,
  ITopModelsPayload,
  ITopModelsResponse,
  IUpdateGradeChangeRequestPayload,
  IUpdateGradeChangeRequestResponse,
} from './types';
import {
  createGradeChangeRequestUrl,
  creditLimitUrl,
  dealDetailsUrl,
  dealsListUrl,
  DeviceGradeConditionUrl,
  DeviceItemConditionUrl,
  downloadAppraisalsUrl,
  fetchAppraisalDetailsUrl,
  fetchAppraisalsUrl,
  fetchDealsForGradeRequestSearchUrl,
  fetchExportDealsExcelUrl,
  fetchGradeReviewDetailsUrl,
  fetchGradeReviewHistoryUrl,
  fetchGradeReviewsListUrl,
  getBidExecutionTimeUrl,
  getSignedUrlEndpoint,
  inventorySummaryUrl,
  topBrandsUrl,
  topModelsUrl,
  updateGradeChangeRequestUrl,
  uploadFileUrl,
} from './urls';

/**
 * This is provides the API client methods for customer service section,
 * it will provider top brands, top models, dashboard statistics apis etc...
 */
export class CustomerServiceApiClient extends ApiClient {
  private static classInstance?: CustomerServiceApiClient;

  private constructor() {
    super({
      apiBaseURL: 'CUSTOMER_SERVICE_BASE_URL',
    });
  }

  /**
   * Applying the dreaded singleton pattern here to reuse the axios instance.
   */
  public static getClientInstance = () => {
    if (!this.classInstance) {
      this.classInstance = new CustomerServiceApiClient();
    }

    return this.classInstance;
  };

  /**
   * Api call to fetch the top brands.
   *
   * @returns IFetchAllBidsResponse
   */
  public fetchTopBrands = async (payload: IFetchTopBrandsPayload) => {
    const response = await this.post<IFetchAllTopBrands>(
      topBrandsUrl(),
      payload
    );

    if (!response.success) throw response;

    return response;
  };

  /**
   * Fetch all top models.
   */
  public fetchAllTopModels = async (payload: ITopModelsPayload) => {
    const response = await this.post<ITopModelsResponse>(
      topModelsUrl(),
      payload
    );

    if (!response.success) throw response;

    return formatTopModelType(response.data.data);
  };

  public fetchInventorySummary = async (payload: IFetchSummaryPayload) => {
    const response = await this.post<IFetchSummary>(
      inventorySummaryUrl(),
      payload
    );

    if (!response.success) throw response;

    return response?.data?.data;
  };

  /**
   * API call to fetch all the deals list using the deal ID.
   */
  public fetchDealsList = async (queryParams: IFetchDealsUrlOptions) => {
    const response = await this.get<IDealsListResponse>(
      dealsListUrl(queryParams)
    );

    if (!response.success) throw response;

    return formatDealsListResponse(response.data.data);
  };

  /**
   * API call to fetch the details of a deal using the deal ID.
   */
  public fetchDealDetails = async (appraisalCode: string) => {
    const response = await this.get<IDealDetailsResponse>(
      dealDetailsUrl(appraisalCode)
    );

    if (!response.success) throw response;

    return formatDealDetailsResponse(response.data.data);
  };

  //----------------------------------------------------------------------------
  // ------------------Dealer Sign up upload file API call----------------------
  //----------------------------------------------------------------------------

  /**
   * API call to upload files such as license, VAT certificate in the
   * registration screen.
   * @deprecated - Please use the common `useImageVideoUploadMutation`
   */
  public userRegistrationFileUpload = async (payload: IFileUploadPayload) => {
    const response = await this.post<IFileUploadData>(
      uploadFileUrl(),
      isWeb ? payload : payload.file,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        requiresAuth: false,
        timeout: 1 * 60 * 1000, // Force a 1 minute wait on uploads.
      }
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * Api call to fetch the appraisals.
   */
  public fetchAppraisals = async (
    queryParams: IFetchPaginatedAppraisalsPayload
  ) => {
    const response = await this.get<IFetchAppraisalsResponse>(
      fetchAppraisalsUrl(queryParams)
    );

    if (!response.success) throw response;

    return response.data?.data;
  };

  /**
   * Api call to download appraisals excel.
   */
  public downloadAppraisals = async (
    queryParams: IFetchPaginatedAppraisalsPayload
  ) => {
    const response = await this.get<BlobPart>(
      downloadAppraisalsUrl(queryParams),
      {
        responseType: 'blob',
      }
    );

    if (!response.success) throw response;

    return response;
  };

  /**
   * Api call to fetch the appraisal details.
   */
  public fetchAppraisalDetails = async (appraisalId: string) => {
    const response = await this.get<IFetchAppraisalDetailsResponse>(
      fetchAppraisalDetailsUrl(appraisalId)
    );

    if (!response.success) throw response;

    return formatAppraisalDetails(response.data?.data);
  };

  /**
   * Api call to get the signed url.
   */
  public getSignedUrl = async (fileName: string) => {
    const response = await this.post<IGetSingedUrlResponse>(
      getSignedUrlEndpoint(),
      { fileName }
    );
    if (!response.success) throw response;

    return response.data;
  };

  /**
   * Api call to download appraisals excel.
   */
  public getBidExecutionTime = async () => {
    const response = await this.get<IBidExecutionTimeResponse>(
      getBidExecutionTimeUrl()
    );

    if (!response.success) throw response;

    return response.data?.data;
  };

  /**
   * Api call to fetch the creditLimit.
   */
  public fetchCreditLimit = async (storeId: string) => {
    const response = await this.get<ICreditLimitResponse>(
      creditLimitUrl(storeId),
      {
        timeout: 3 * 60 * 1000, // Force timeout to 3 minutes.
      }
    );

    if (!response.success) throw response;

    return formatCreditLimitResponse(response.data?.data);
  };

  /**
   * API call to fetch the grade review lists.
   */
  public fetchGradeReviewList = async (
    queryParams: IFetchGradeReviewUrlOptions
  ) => {
    const response = await this.get<IGradeReviewRequestListResponse>(
      fetchGradeReviewsListUrl(queryParams)
    );

    if (!response.success) throw response;

    return formatGradeReviewRequestListResponse(response.data.data);
  };

  /**
   * API call to fetch the grade review request details.
   */
  public fetchGradeReviewDetails = async (id: string) => {
    const response = await this.get<IGradeReviewDetailsResponse>(
      fetchGradeReviewDetailsUrl(id)
    );

    if (!response.success) throw response;

    return formatGradeReviewRequestDetailsResponse(response.data.data);
  };

  /**
   * Api call to fetch the Device Grade Conditions.
   */
  public fetchDeviceGradeConditions = async (
    queryParams: IFetchDeviceGradeConditionUrlOptions
  ) => {
    const response = await this.get<IDeviceGradeConditionRawResponse>(
      DeviceGradeConditionUrl(queryParams)
    );

    if (!response.success) throw response;

    return formatDeviceGradeChangeResponse(response.data);
  };

  /**
   * Api call to fetch the Device Item Conditions.
   * TODO: Change the param and response types.
   */
  public fetchDeviceItemConditions = async (
    queryParams: IFetchDeviceGradeConditionUrlOptions
  ) => {
    const response = await this.get<IDeviceItemConditionRawResponse>(
      DeviceItemConditionUrl(queryParams)
    );

    if (!response.success) throw response;

    return formatDeviceItemConditionResponse(response.data);
  };

  /**
   * API call to fetch the grade review lists.
   */
  public fetchDealsForGradeRequestSearchList = async (
    queryParams: IFetchGradeDealsUrlOptions
  ) => {
    const response = await this.get<IGradeDealsSearchRawResponse>(
      fetchDealsForGradeRequestSearchUrl(queryParams)
    );

    if (!response.success) throw response;

    return formatRequestGradeDealsSearchResponse(response.data.data);
  };

  /**
   * API call to fetch the grade review request history.
   */
  public fetchGradeReviewHistory = async (id: string) => {
    const response = await this.get<IGradeReviewHistoryResponse>(
      fetchGradeReviewHistoryUrl(id)
    );

    if (!response.success) throw response;

    return formatGradeReviewRequestHistoryResponse(response.data.data);
  };

  /**
   * Api to create grade change request.
   */
  public createGradeChangeRequest = async (
    payload: ICreateGradeChangeRequestPayload
  ) => {
    const response = await this.post<ICreateGradeChangeRequestResponse>(
      createGradeChangeRequestUrl(),
      payload
    );
    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api to update grade change request.
   */
  public updateGradeChangeRequest = async (
    payload: IUpdateGradeChangeRequestPayload
  ) => {
    const response = await this.post<IUpdateGradeChangeRequestResponse>(
      updateGradeChangeRequestUrl(),
      payload,
      {
        timeout: 2 * 60 * 1000, // Force a 2 minute wait on update grade change request.
      }
    );
    if (!response.success) throw response;

    return response.data;
  };

  /**
   * API call to export deals data to Excel.
   */
  public exportDealsExcel = async (queryParams: IExportDealsQueryOptions) => {
    const response = await this.get<BlobPart>(
      fetchExportDealsExcelUrl(queryParams),
      {
        responseType: 'blob',
        timeout: 60 * 1000,
      }
    );

    if (!response.success) throw response;

    return response.data;
  };
}

/**
 * This creates a new instance of the class. is th base Axios API client Class
 * wrapper for All User Customer Service related requests.
 */
export const CUSTOMER_SERVICE_API =
  CustomerServiceApiClient.getClientInstance();
